import { useState, useCallback } from 'react';

/**
 * Custom hook for managing notification state
 * Provides a simple API to show notifications
 */
const useNotification = () => {
  const [notification, setNotification] = useState({
    isOpen: false,
    title: '',
    message: '',
    variant: 'info',
    duration: 5000
  });

  const showNotification = useCallback(({ title, message, variant = 'info', duration = 5000 }) => {
    setNotification({
      isOpen: true,
      title,
      message,
      variant,
      duration
    });
  }, []);

  const hideNotification = useCallback(() => {
    setNotification(prev => ({ ...prev, isOpen: false }));
  }, []);

  // Convenience methods
  const showSuccess = useCallback((message, title = 'Success') => {
    showNotification({ title, message, variant: 'success' });
  }, [showNotification]);

  const showError = useCallback((message, title = 'Error') => {
    showNotification({ title, message, variant: 'error' });
  }, [showNotification]);

  const showWarning = useCallback((message, title = 'Warning') => {
    showNotification({ title, message, variant: 'warning' });
  }, [showNotification]);

  const showInfo = useCallback((message, title = 'Info') => {
    showNotification({ title, message, variant: 'info' });
  }, [showNotification]);

  return {
    notification,
    showNotification,
    hideNotification,
    showSuccess,
    showError,
    showWarning,
    showInfo
  };
};

export default useNotification;
